import produce from "immer";
import { CustomizationState } from "../";
import { ContentDateText } from "../../../types/customization/content";
import { daysArray, getDatesListFromDate } from "../../../utils/customization/calendar";
import { findColorDefinition, findPageDefinition } from "../../../utils/customization/finders";

export interface SetCalendarInitialContentAction {
  type: "setCalendarInitialContent";
  date?: string;
}

export function setCalendarInitialContent(
  state: CustomizationState,
  action: SetCalendarInitialContentAction
): CustomizationState {
  return produce(state, (draftState) => {
    const { template, content } = draftState;

    if (template.templateType !== "calendar" || content.templateType !== "calendar") {
      return;
    }

    const startingDate = template.startingDate || action.date;
    if (!startingDate) {
      return;
    }

    if (!content.startingDate) {
      content.startingDate = startingDate;
    }

    const datesConfiguration = getDatesListFromDate(startingDate);

    if (!content.dateTitle) {
      const firstYear = datesConfiguration[0].year;
      const lastYear = datesConfiguration[datesConfiguration.length - 1].year;
      content.dateTitle =
        firstYear !== lastYear ? `${firstYear} - ${lastYear}` : firstYear.toString();
    }

    content.pages.map((page) => {
      const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);
      if (pageDefinition.type != "month") {
        return page;
      }

      const currentMonthDatas = datesConfiguration.shift();
      if (!currentMonthDatas) {
        return page;
      }

      const pageColorDefinition = findColorDefinition(
        page.backgroundColorKey,
        template.colorDefinitions
      );

      pageDefinition.elementDefinitions.forEach((element) => {
        if (element.type === "date-text") {
          if (!page.dateTexts) {
            page.dateTexts = [];
          }
          const elementContent = page.dateTexts.find(
            (dateText) => dateText.dateTextKey === element.key
          );

          if (!elementContent) {
            if (element.contentType === "month") {
              const monthDefinition = template.dateDefinitions.find(
                (dateDefinition) => dateDefinition.key === element.dateDefinitionKey
              );
              if (monthDefinition) {
                const dateText: ContentDateText = {
                  contentKey: currentMonthDatas.month,
                  dateTextKey: element.key,
                };

                if (monthDefinition.suffixFormat) {
                  const suffix = monthDefinition.suffixFormat
                    .replace("YYYY", currentMonthDatas.year.toString())
                    .replace("YY", currentMonthDatas.year.toString().slice(-2));
                  dateText.suffix = suffix;
                }
                page.dateTexts.push(dateText);
              }
            } else if (element.contentType === "day") {
              const dayNumber = element.order > 6 ? 0 : element.order;
              page.dateTexts.push({
                contentKey: daysArray[dayNumber],
                dateTextKey: element.key,
              });
            }
          }
        }

        if (element.type === "date-cell") {
          if (!page.dateCells) {
            page.dateCells = [];
          }

          let dayCellContent = page.dateCells.find(
            (dateCell) => dateCell.dateCellKey === element.key
          );

          const dayPosition = element.order - 1;
          if (dayPosition >= currentMonthDatas.days.length) {
            return;
          }
          const daysDatas = currentMonthDatas.days[dayPosition];

          const dateCellForcedNumberDefinition = element.elements.find(
            (dateElement) =>
              dateElement.type === "forced-text" &&
              dateElement.interpolatedContent === "value_number"
          );

          const dateCellForcedNumber = dateCellForcedNumberDefinition && {
            childKey: dateCellForcedNumberDefinition.key,
            valueNumber: daysDatas.date.toString(),
          };

          if (!dayCellContent) {
            dayCellContent = {
              cellColor: {
                key: pageColorDefinition?.cellColors ? pageColorDefinition.cellColors[0].key : "",
                state: daysDatas.active ? "active" : "inactive",
              },
              dateCellKey: element.key,
              interpolatedValues: [],
            };
            if (dateCellForcedNumber) {
              dayCellContent.interpolatedValues[0] = dateCellForcedNumber;
            }
            page.dateCells.push(dayCellContent);
          }
        }
      });
    });
  });
}
